import { createContext, useContext, useState, useEffect, useCallback } from "react";

/* ============================================================
   Live FX rates
   ============================================================
   API: frankfurter.app — ECB rates, free, no key, CORS enabled.
   Updated once per business day. Good enough for our use case.
   
   Strategy:
   1. On mount: read cache from localStorage
   2. If cache fresh (<1h), use it immediately
   3. Fetch live in background
   4. Fall back to FX_RATES constants on any error
   ============================================================ */

const STORAGE_KEY = "anj-fx-cache";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const API_URL = "https://api.frankfurter.app/latest?from=JPY&to=BRL,USD";

// Fallback rates if API fails — last manually verified May 2026
const FALLBACK_RATES = {
  JPY_TO_BRL: 0.039,
  JPY_TO_USD: 0.0065,
  USD_TO_BRL: 5.85,
  BRL_TO_JPY: 25.6,
  updated: null,
  source: "fallback",
};

const FxContext = createContext(null);

function readCache() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    const age = Date.now() - parsed.timestamp;
    if (age > CACHE_TTL_MS) return { ...parsed.rates, stale: true };
    return { ...parsed.rates, stale: false };
  } catch (_) {
    return null;
  }
}

function writeCache(rates) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ timestamp: Date.now(), rates })
    );
  } catch (_) {
    // localStorage blocked — ignore, just won't cache
  }
}

async function fetchRates() {
  const res = await fetch(API_URL, { method: "GET" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  // Frankfurter format: { amount: 1, base: "JPY", date: "2026-05-15", rates: { BRL: 0.0389, USD: 0.0065 } }
  if (!data?.rates?.BRL || !data?.rates?.USD) {
    throw new Error("Invalid API response");
  }
  return {
    JPY_TO_BRL: data.rates.BRL,
    JPY_TO_USD: data.rates.USD,
    USD_TO_BRL: data.rates.BRL / data.rates.USD,
    BRL_TO_JPY: 1 / data.rates.BRL,
    updated: data.date,
    source: "frankfurter",
  };
}

export function FxProvider({ children }) {
  // Start with cache if any, otherwise fallback
  const [rates, setRates] = useState(() => {
    const cached = readCache();
    if (cached) return cached;
    return FALLBACK_RATES;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await fetchRates();
      setRates(fresh);
      writeCache(fresh);
    } catch (e) {
      setError(e.message);
      // Keep whatever we had (cache or fallback)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we don't have cached data, or it's stale, fetch
    const cached = readCache();
    if (!cached || cached.stale) {
      refetch();
    }
  }, [refetch]);

  return (
    <FxContext.Provider value={{ rates, loading, error, refetch }}>
      {children}
    </FxContext.Provider>
  );
}

export function useFxRates() {
  const ctx = useContext(FxContext);
  if (!ctx) throw new Error("useFxRates must be used inside FxProvider");
  return ctx;
}
